// Authentication service for JSON-based local storage login
class AuthService {
  constructor() {
    this.storageKey = 'etm_auth_user';
    this.approvedUsersKey = 'approved_users';
    this.initializeStorage();
  }

  // Initialize storage with default admin user
  initializeStorage() {
    if (!localStorage.getItem(this.approvedUsersKey)) {
      localStorage.setItem(this.approvedUsersKey, JSON.stringify([]));
    }
  }

  // Validate student ID format
  isValidStudentId(studentId) {
    return /^4\d{4,}$/.test(studentId);
  }

  // Get approved users
  getApprovedUsers() {
    return JSON.parse(localStorage.getItem(this.approvedUsersKey) || '[]');
  }
  saveApprovedUsers(users) {
    localStorage.setItem(this.approvedUsersKey, JSON.stringify(users));
  }

  // Register method
  async register(registrationData) {
    // Debug: Log what data is received
    console.log('AuthService register received:', registrationData);
    
    const { fullName, password, batch } = registrationData;
    
    // Debug: Log destructured data
    console.log('Destructured data:', { fullName, password, batch });
    
    // Validate required fields
    if (!fullName || fullName.trim().length < 2) {
      throw new Error('Full name is required');
    }
    
    if (!password || password.length < 6) {
      throw new Error('Password must be at least 6 characters');
    }
    
    if (!batch) {
      throw new Error('Batch is required');
    }

    const approvedUsers = this.getApprovedUsers();
    
    // Generate unique student ID based on batch
    let studentId;
    let attempts = 0;
    const maxAttempts = 100;
    do {
      studentId = `${batch}${Math.floor(Math.random() * 10000).toString().padStart(4, '0')}`;
      attempts++;
      // Check if this ID already exists
      const existingApprovedId = approvedUsers.find(user => user.studentId === studentId);
      if (!existingApprovedId) {
        break; // Unique ID found
      }
    } while (attempts < maxAttempts);
    if (attempts >= maxAttempts) {
      throw new Error('Unable to generate unique student ID. Please try again.');
    }
    // Create new approved user
    const newUser = {
      id: `user_${Date.now()}`,
      studentId,
      fullName: fullName.trim(),
      password: password, // In production, this should be hashed
      batch,
      role: 'student',
      registeredAt: new Date().toISOString()
    };
    approvedUsers.push(newUser);
    this.saveApprovedUsers(approvedUsers);
    localStorage.setItem(this.storageKey, JSON.stringify(newUser));
    return newUser;
  }

  // Login with email and password
  async loginWithEmail(email, password) {
    if (!email || !password) {
      throw new Error('Email and password are required');
    }

    const approvedUsers = this.getApprovedUsers();
    // Check if user is approved
    const approvedUser = approvedUsers.find(user => user.email === email);
    if (approvedUser) {
      // In production, you would hash and compare passwords
      if (approvedUser.password === password) {
        const user = {
          ...approvedUser,
          lastLogin: new Date().toISOString()
        };
        localStorage.setItem(this.storageKey, JSON.stringify(user));
        return user;
      } else {
        throw new Error('Invalid password');
      }
    }
    throw new Error('User not found. Please register first.');
  }

  // Login method (original - with student ID and name)
  async login(studentId, fullName) {
    if (!this.isValidStudentId(studentId)) {
      throw new Error('Student ID must start with "4" and be at least 5 digits long');
    }

    if (!fullName || fullName.trim().length < 2) {
      throw new Error('Full name is required');
    }

    const approvedUsers = this.getApprovedUsers();
    // Check if user is already approved
    const approvedUser = approvedUsers.find(user => user.studentId === studentId && user.fullName === fullName.trim());
    if (approvedUser) {
      const user = {
        ...approvedUser,
        lastLogin: new Date().toISOString()
      };
      localStorage.setItem(this.storageKey, JSON.stringify(user));
      return user;
    }
    throw new Error('User not found. Please register first.');
  }

  // Get current user
  async getCurrentUser() {
    const userData = localStorage.getItem(this.storageKey);
    if (!userData) return null;

    try {
      const user = JSON.parse(userData);
      return user;
    } catch (error) {
      console.error('Error parsing user data:', error);
      this.logout();
      return null;
    }
  }

  // Logout method
  async logout() {
    localStorage.removeItem(this.storageKey);
    // Removed call to this.clearToken() as it does not exist
  }

}

const authService = new AuthService();
export default authService;
